import React, { useState, useEffect, useRef } from 'react';
import Modal from 'react-modal';
import Camera, { FACING_MODES } from 'react-html5-camera-photo';
import 'react-html5-camera-photo/build/css/index.css';
import { showErrorToast } from '../../utils/toastHelper';
import { IconButton, Button, Box } from '@material-ui/core';
import { Close, Check, Refresh } from '@material-ui/icons';
import { i18n } from '../../translate/i18n';

Modal.setAppElement('#root');

const modalOverlayStyles = {
  overlay: {
    backgroundColor: 'rgba(0, 0, 0, 0.85)',
    zIndex: 1400,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  content: {
    position: 'relative',
    width: '90%',
    maxWidth: '900px',
    height: '90vh',
    maxHeight: '800px',
    top: 'auto',
    left: 'auto',
    right: 'auto',
    bottom: 'auto',
    transform: 'none',
    borderRadius: '16px',
    boxShadow: '0 20px 60px rgba(0, 0, 0, 0.5)',
    border: 'none',
    padding: '0',
    background: '#000',
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
  },
};

const cameraWrapperStyles = {
  position: 'relative',
  width: '100%',
  height: '100%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  backgroundColor: '#000',
  overflow: 'hidden',
};

const previewContainerStyles = {
  position: 'relative',
  width: '100%',
  height: '100%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  backgroundColor: '#000',
  padding: '20px',
  boxSizing: 'border-box',
};

const previewImageStyles = {
  maxWidth: '100%',
  maxHeight: 'calc(100% - 120px)',
  objectFit: 'contain',
  borderRadius: '8px',
};

const controlsContainerStyles = {
  position: 'absolute',
  bottom: 0,
  left: 0,
  right: 0,
  padding: '24px',
  background: 'linear-gradient(to top, rgba(0,0,0,0.9) 0%, rgba(0,0,0,0.5) 50%, transparent 100%)',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  gap: '16px',
  zIndex: 10,
};

const closeButtonStyles = {
  position: 'absolute',
  top: '16px',
  right: '16px',
  zIndex: 1000,
  backgroundColor: 'rgba(0, 0, 0, 0.7)',
  color: '#fff',
  width: '40px',
  height: '40px',
  borderRadius: '50%',
};

const actionButtonStyles = {
  minWidth: '56px',
  height: '56px',
  borderRadius: '50%',
  backgroundColor: 'rgba(255, 255, 255, 0.2)',
  color: '#fff',
  border: 'none',
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  transition: 'all 0.2s ease',
};

const confirmButtonStyles = {
  minWidth: '72px',
  height: '72px',
  borderRadius: '50%',
  backgroundColor: '#4CAF50',
  color: '#fff',
  border: '4px solid rgba(255, 255, 255, 0.3)',
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  boxShadow: '0 4px 20px rgba(76, 175, 80, 0.4)',
  transition: 'all 0.2s ease',
};

const captureButtonStyles = {
  position: 'absolute',
  bottom: '24px',
  left: '50%',
  transform: 'translateX(-50%)',
  zIndex: 100,
  width: '72px',
  height: '72px',
  borderRadius: '50%',
  backgroundColor: '#fff',
  border: '4px solid rgba(255, 255, 255, 0.5)',
  boxShadow: '0 4px 20px rgba(0, 0, 0, 0.4)',
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: 0,
};

const ModalCamera = ({ isOpen, onRequestClose, onCapture }) => {
  const [capturedImage, setCapturedImage] = useState(null);
  const videoRef = useRef(null);

  useEffect(() => {
    if (isOpen) {
      // Adicionar estilos customizados para corrigir distorções e esconder botão padrão
      const style = document.createElement('style');
      style.id = 'camera-modal-custom-styles';
      style.textContent = `
        /* Corrigir distorções do vídeo */
        .react-html5-camera-photo > video,
        .react-html5-camera-photo > img {
          width: 100% !important;
          height: 100% !important;
          object-fit: contain !important;
          object-position: center !important;
        }
        .react-html5-camera-photo {
          width: 100% !important;
          height: 100% !important;
          display: flex !important;
          align-items: center !important;
          justify-content: center !important;
          background-color: #000 !important;
          position: relative !important;
        }
        .react-html5-camera-photo > div {
          width: 100% !important;
          height: 100% !important;
          display: flex !important;
          align-items: center !important;
          justify-content: center !important;
        }
        
        /* ESCONDER BOTÃO PADRÃO DA BIBLIOTECA */
        .react-html5-camera-photo > button,
        .react-html5-camera-photo > div > button,
        .react-html5-camera-photo button[type="button"],
        .react-html5-camera-photo button {
          display: none !important;
          visibility: hidden !important;
        }
      `;
      document.head.appendChild(style);
      
      // Encontrar o vídeo quando for renderizado
      const findVideo = () => {
        const video = document.querySelector('.react-html5-camera-photo video');
        if (video) {
          videoRef.current = video;
        }
      };
      
      setTimeout(findVideo, 100);
      setTimeout(findVideo, 500);
      setTimeout(findVideo, 1000);
      
      return () => {
        const existingStyle = document.getElementById('camera-modal-custom-styles');
        if (existingStyle) {
          existingStyle.remove();
        }
      };
    } else {
      const existingStyle = document.getElementById('camera-modal-custom-styles');
      if (existingStyle) {
        existingStyle.remove();
      }
    }
  }, [isOpen]);

  const handleCaptureClick = () => {
    const video = videoRef.current || document.querySelector('.react-html5-camera-photo video');
    if (video && video.readyState === video.HAVE_ENOUGH_DATA) {
      try {
        const canvas = document.createElement('canvas');
        canvas.width = video.videoWidth;
        canvas.height = video.videoHeight;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(video, 0, 0);
        const dataUri = canvas.toDataURL('image/jpeg', 0.92);
        handleTakePhoto(dataUri);
      } catch (error) {
        console.error('Erro ao capturar foto:', error);
        // Tentar usar o botão padrão como fallback
        const defaultButton = document.querySelector('.react-html5-camera-photo button');
        if (defaultButton) {
          defaultButton.click();
        }
      }
    } else {
      // Tentar usar o botão padrão como fallback
      const defaultButton = document.querySelector('.react-html5-camera-photo button');
      if (defaultButton) {
        defaultButton.click();
      }
    }
  };

  const handleTakePhoto = (dataUri) => {
    setCapturedImage(dataUri);
  };

  const handleCameraError = (error) => {
    console.error("Erro ao acessar a câmera:", error);
    
    let errorMessage = i18n.t("cameraModal.errors.access");
    
    if (error.name === 'NotFoundError' || error.name === 'DevicesNotFoundError') {
      errorMessage = i18n.t("cameraModal.errors.notFound");
    } else if (error.name === 'NotAllowedError' || error.name === 'PermissionDeniedError') {
      errorMessage = i18n.t("cameraModal.errors.permissionDenied");
    } else if (error.name === 'NotReadableError' || error.name === 'TrackStartError') {
      errorMessage = i18n.t("cameraModal.errors.inUse");
    } else if (error.name === 'OverconstrainedError' || error.name === 'ConstraintNotSatisfiedError') {
      errorMessage = i18n.t("cameraModal.errors.unsupported");
    } else if (error.message) {
      if (error.message.includes('Requested device not found') || error.message.includes('device not found')) {
        errorMessage = i18n.t("cameraModal.errors.notFound");
      } else {
        errorMessage = i18n.t("cameraModal.errors.accessWithDetails", { details: error.message });
      }
    }
    
    showErrorToast(errorMessage, {
      autoClose: 5000,
      toastId: 'camera-error',
    });
    
    onRequestClose();
  };

  const handleConfirm = () => {
    if (!capturedImage) return;

    fetch(capturedImage)
      .then((res) => res.blob())
      .then((blob) => {
        onCapture(blob);
      });

    setCapturedImage(null);
    onRequestClose();
  };

  const handleRetake = () => {
    setCapturedImage(null);
  };

  const handleClose = () => {
    setCapturedImage(null);
    onRequestClose();
  };

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={handleClose}
      style={modalOverlayStyles}
      closeTimeoutMS={200}
    >
      {/* BOTÃO X - CÍRCULO AMARELO (TOPO DIREITO) */}
      <IconButton
        onClick={handleClose}
        style={closeButtonStyles}
        aria-label={i18n.t("common.buttons.close")}
      >
        <Close />
      </IconButton>

      {capturedImage ? (
        <Box style={previewContainerStyles}>
          <img
            src={capturedImage}
            alt={i18n.t("cameraModal.previewAlt")}
            style={previewImageStyles}
          />
          <Box style={controlsContainerStyles}>
            <Button
              onClick={handleRetake}
              style={actionButtonStyles}
              aria-label={i18n.t("cameraModal.retakePhoto")}
            >
              <Refresh style={{ color: '#fff', fontSize: '24px' }} />
            </Button>
            <Button
              onClick={handleConfirm}
              style={confirmButtonStyles}
              aria-label={i18n.t("cameraModal.confirmPhoto")}
            >
              <Check style={{ color: '#fff', fontSize: '32px' }} />
            </Button>
            <div style={{ width: '56px' }} />
          </Box>
        </Box>
      ) : (
        <Box style={cameraWrapperStyles}>
          <Camera
            onTakePhoto={handleTakePhoto}
            onCameraError={handleCameraError}
            idealFacingMode={FACING_MODES.ENVIRONMENT}
            isImageMirror={false}
            sizeFactor={1}
            imageType="image/jpeg"
            imageCompression={0.92}
          />
          {/* BOTÃO DE CAPTURA CUSTOMIZADO - CÍRCULO BRANCO ABAIXO DA FOTO */}
          <Button
            onClick={handleCaptureClick}
            style={captureButtonStyles}
            aria-label={i18n.t("cameraModal.capturePhoto")}
          >
            <div
              style={{
                width: '56px',
                height: '56px',
                borderRadius: '50%',
                backgroundColor: '#fff',
                border: '2px solid rgba(0, 0, 0, 0.1)',
              }}
            />
          </Button>
        </Box>
      )}
    </Modal>
  );
};

export default ModalCamera;
